import React from 'react';
import { makeStyles } from '@mui/styles';
import Carousel from 'nuka-carousel';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';

const data = [
  {
    title: 'Acknowledging',
    text: 'This type of paraphrasing is done to show your understanding/comprehension, usually with the intent to answer a question. If you can’t paraphrase the information clearly and easily, there is more work to be done.',
    color: '#6CB9FF',
  },
  {
    title: 'Organizing',
    text: 'This type of paraphrasing is done to explain a series of ideas or events with fewer details for a better overview of the big picture. It often involves making a numbered list or outline with a clear beginning, middle, and end to organize the information.',
    color: '#FFCF50',
  },
  {
    title: 'Changing Focus',
    text: 'Paraphrasing is a great way to change the focus level of information by rephrasing it to be more abstract and big-picture or more concrete and specific. Use it to connect with different audiences to convey information in a way that will resonate with them easily.',
    color: '#61C774',
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#DDEFFF',
    overflow: 'hidden',
  },
  container: {
    maxWidth: 665,
    margin: '0 auto',
    padding: '60px 15px',
    [theme.breakpoints.down('sm')]: {
      padding: '60px 32px',
    },
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 600,
    margin: '0 0 20px',
  },
  sectionText: {
    fontSize: 16,
  },
  typeCardsList: {
    marginTop: 36,
    [theme.breakpoints.down('sm')]: {
      margin: '36px -32px 0',
    },
  },
  mobileSlider: {
    // margin: '0 -16px',
    '& .slider-slide': {
      padding: '16px',
    },
    '& .slider-control-bottomcenter': {
      bottom: '-10px !important',
      '& .paging-item button svg': {
        display: 'none',
      },
      '& .paging-item': {
        marginLeft: 6,
      },
      '& .paging-item button': {
        width: '8px',
        height: '8px',
        borderRadius: '100px',
        opacity: '1 !important',
        backgroundColor: `${theme.specialColors.CIRCULAR_PROGRESS}  !important`,
        padding: 0,
      },
      '& .paging-item.active button': {
        backgroundColor: `${theme.palette.primary.main} !important`,
        width: '18px',
        borderRadius: '50px',
      },
    },
  },
  typeCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 36px 16px 16px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    borderRadius: 100,
    marginBottom: 28,
    '&:last-child': {
      marginBottom: 0,
    },
    '&:nth-child(2n)': {
      flexDirection: 'row-reverse',
      padding: '16px 16px 16px 36px',
      '& div:first-child': {
        marginRight: 0,
        marginLeft: 20,
        [theme.breakpoints.down('sm')]: {
          marginLeft: 0,
          marginTop: 20,
        },
      },
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      marginBottom: 0,
      padding: '16px 32px 40px',
      minHeight: '310px',
      borderRadius: 20,
    },
  },
  cardTitle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '192px',
    height: '110px',
    fontSize: '19px',
    fontWeight: 600,
    borderRadius: 100,
    marginRight: 20,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginBottom: 20,
    },
  },
  cardText: {
    lineHeight: '19px',
    flex: 1,
  },
}));

function Types() {
  const isMobile = useSelector((state: RootState) => state.common.platform.isMobile);
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <h2 className={classes.sectionTitle}>The Three Types of Paraphrasing</h2>
        <div className={classes.sectionText}>
          The three types of paraphrasing are based on the intention of the writer.
        </div>
        {isMobile ? (
          <div className={classes.typeCardsList}>
            <Carousel
              className={classes.mobileSlider}
              slideWidth={1}
              cellSpacing={16}
              renderCenterLeftControls={null}
              renderCenterRightControls={null}
              framePadding="0"
              frameOverflow="visible"
            >
              {data.map((item) => (
                <TypeCard key={item.title} title={item.title} text={item.text} color={item.color} />
              ))}
            </Carousel>
          </div>
        ) : (
          <div className={classes.typeCardsList}>
            {data.map((item) => (
              <TypeCard key={item.title} title={item.title} text={item.text} color={item.color} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Types;

function TypeCard({ title, text, color }: any) {
  const classes = useStyles();

  return (
    <div className={classes.typeCard}>
      <div className={classes.cardTitle} style={{ backgroundColor: color }}>
        {title}
      </div>
      <div className={classes.cardText}>{text}</div>
    </div>
  );
}
